import { Link, useNavigate } from 'react-router-dom';
import { Settings, MapPin, Building, Star, FileText, Image, Award, LogOut, Monitor, Eye, Layout, AlignJustify, Heart, ImageIcon, Info, TrendingUp, FileCheck, Search, Users2, Mail, FormInput, Megaphone, Compass, Share2, Map, Code, Webhook, FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { removeAuthToken } from '@/utils/auth';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const navigate = useNavigate();
  
  const sections = [
    { icon: Settings, title: 'Site Settings', description: 'Manage logos and contact info', link: '/admin/settings', color: 'bg-blue-500' },
    { icon: Layout, title: 'Header Settings', description: 'Edit header, logo & navigation', link: '/admin/header', color: 'bg-cyan-500' },
    { icon: AlignJustify, title: 'Footer Settings', description: 'Edit footer content & links', link: '/admin/footer', color: 'bg-slate-500' },
    { icon: Share2, title: 'Social Media', description: 'Manage floating social media icons', link: '/admin/social-media', color: 'bg-indigo-600' },
    { icon: Eye, title: 'Section Visibility', description: 'Show/hide homepage sections', link: '/admin/sections', color: 'bg-teal-500' },
    { icon: Monitor, title: 'Hero Slider', description: 'Manage homepage slider backgrounds', link: '/admin/hero-slides', color: 'bg-orange-500' },
    { icon: Megaphone, title: 'Popup Settings', description: 'Configure website popup with custom form', link: '/admin/popup-settings', color: 'bg-purple-600' },
    { icon: Info, title: 'About Content', description: 'Edit "About Us" section text', link: '/admin/about-content', color: 'bg-sky-500' },
    { icon: FileCheck, title: 'About Page', description: 'Manage full /about page content', link: '/admin/about-page', color: 'bg-violet-500' },
    { icon: Compass, title: 'Find Space Page', description: 'Edit Find a Space page header & image', link: '/admin/find-space-page', color: 'bg-teal-600' },
    { icon: Map, title: 'Find Us Section', description: 'Edit Find Us (Map) section header & image', link: '/admin/find-us', color: 'bg-green-600' },
    { icon: TrendingUp, title: 'Milestones', description: 'Edit statistics (50,000+, 2, 150+)', link: '/admin/milestones', color: 'bg-amber-500' },
    { icon: Search, title: 'SEO Settings', description: 'Page titles, meta tags, Open Graph', link: '/admin/seo', color: 'bg-emerald-500' },
    { icon: Code, title: 'Site Scripts', description: 'Google Analytics, Search Console scripts', link: '/admin/site-scripts', color: 'bg-purple-600' },
    { icon: Users2, title: 'Lead Center', description: 'View leads, email & webhook integration', link: '/admin/leads', color: 'bg-cyan-600' },
    { icon: Webhook, title: 'Salesforce Webhook', description: 'Integrate with Salesforce or any CRM', link: '/admin/salesforce-webhook', color: 'bg-orange-600' },
    { icon: Mail, title: 'Email Settings', description: 'Configure SMTP for notifications', link: '/admin/email-settings', color: 'bg-blue-600' },
    { icon: FormInput, title: 'Contact Form', description: 'Edit contact form fields', link: '/admin/contact-form', color: 'bg-lime-600' },
    { icon: Heart, title: 'Care Cards', description: 'Manage "We Care About You" cards', link: '/admin/care-cards', color: 'bg-rose-500' },
    { icon: MapPin, title: 'Locations', description: 'Manage office locations', link: '/admin/locations', color: 'bg-green-500' },
    { icon: Building, title: 'Workspaces', description: 'Manage workspace types', link: '/admin/spaces', color: 'bg-purple-500' },
    { icon: Award, title: 'Amenities', description: 'Manage amenities', link: '/admin/amenities', color: 'bg-yellow-500' },
    { icon: Star, title: 'Testimonials', description: 'Manage client reviews', link: '/admin/testimonials', color: 'bg-pink-500' },
    { icon: FileText, title: 'Blog Posts', description: 'Manage blog content', link: '/admin/blog', color: 'bg-indigo-500' },
    { icon: Image, title: 'Gallery', description: 'Manage gallery images', link: '/admin/gallery', color: 'bg-red-500' },
    { icon: ImageIcon, title: 'Gallery Carousel', description: 'Manage homepage gallery carousel', link: '/admin/gallery-carousel', color: 'bg-fuchsia-500' },
    { icon: FolderOpen, title: 'Library', description: 'Location-wise gallery with images & videos', link: '/admin/library', color: 'bg-rose-600' },
    { icon: AlignJustify, title: 'About Buloke', description: 'Edit About Buloke Realty homepage section', link: '/admin/about-buloke', color: 'bg-amber-600' },
  ];

  const handleLogout = () => {
    removeAuthToken();
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-2">Manage your website content</p>
            </div>
            <Button onClick={handleLogout} variant="outline" className="flex items-center gap-2">
              <LogOut size={18} />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <Link
                key={section.link}
                to={section.link}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all hover:-translate-y-1"
              >
                <div className={`w-12 h-12 ${section.color} rounded-lg flex items-center justify-center mb-4`}>
                  <Icon className="text-white" size={24} />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">{section.title}</h2>
                <p className="text-gray-600 text-sm">{section.description}</p>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}